import { FaUsers, FaArrowUp, FaDollarSign } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
    },
  },
};

const useCountAnimation = (endValue, duration = 2) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime = null;
    const animate = currentTime => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);

      if (endValue.includes('%')) {
        const numericValue = parseInt(endValue);
        setCount(Math.floor(progress * numericValue));
      } else if (endValue.includes('P')) {
        const numericValue = parseInt(endValue.replace(/[^0-9]/g, '')) * 1000000;
        setCount(Math.floor(progress * numericValue));
      } else {
        const numericValue = parseInt(endValue.replace(/[^0-9]/g, ''));
        setCount(Math.floor(progress * numericValue));
      }

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [endValue, duration]);

  return count;
};

const StatItem = ({ item, index }) => {
  const count = useCountAnimation(item.value);
  const formattedValue = item.value.includes('P')
    ? `${(count / 1000000).toFixed(1)}M+`
    : item.value.includes('%')
      ? `${count}%`
      : `${count}+`;

  return (
    <motion.div
      className="flex flex-col items-center justify-center text-center"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      transition={{ type: 'spring', stiffness: 300 }}
      variants={itemVariants}
    >
      <motion.div
        className={`flex h-20 w-20 items-center justify-center rounded-full ${item.bg} mb-6`}
        whileHover={{ scale: 1.1 }}
        transition={{ type: 'spring', stiffness: 300 }}
      >
        {item.icon}
      </motion.div>
      <motion.div
        className="text-3xl font-bold text-gray-900"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {formattedValue}
      </motion.div>
      <div className="text-base text-gray-500">{item.label}</div>
    </motion.div>
  );
};

export default function StatsSection() {
  const stats = [
    {
      icon: <FaUsers className="rounded-full bg-slate-100 p-3 text-4xl text-slate-500 " />,
      value: '500+',
      label: 'Entrepreneurs Supported',
    },
    {
      icon: <FaArrowUp className="rounded-full bg-slate-100 p-3 text-4xl text-slate-500" />,
      bg: 'bg-slate-50',
      value: '85%',
      label: 'Success Rate',
    },
    {
      icon: <FaDollarSign className="rounded-full bg-slate-100 p-3 text-4xl text-slate-500" />,
      bg: 'bg-slate-50',
      value: 'P2M+',
      label: 'Funding Facilitated',
    },
  ];

  return (
    <div className="w-full py-12 md:py-20">
      <motion.div
        className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-6 md:grid-cols-3 md:gap-10 md:px-12"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
      >
        {stats.map((item, idx) => (
          <StatItem key={idx} item={item} index={idx} />
        ))}
      </motion.div>
    </div>
  );
}
